import clsx from "clsx";
import { Children, FC, ReactNode, useRef } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { NoData, RenderWhen } from "../../../../../../../components";
import { usePopover } from "../../../../../../../hooks";
import { useMoreVisibleItems } from "../../../hooks";
import styles from "../info.module.scss";

type Props = {
  data: Array<string>;
  renderTag: (item: string, className?: string) => ReactNode;
  "data-testid"?: string;
};

const getNumberOfShownTags = (fittingCount: number, tagCount: number) => {
  if (!tagCount) return 0;
  return fittingCount || 1;
};

const TagList: FC<Props> = ({
  children,
  data,
  renderTag,
  "data-testid": dataTestId
}) => {
  const [isShow, show, hide] = usePopover();
  const childrenRef = useRef<HTMLDivElement>(null);

  const childrenCount = Children.count(children);
  const itemCount = data.length + Children.count(children);

  const { count, containerRef, moreRef } = useMoreVisibleItems(
    itemCount,
    childrenRef
  );

  const numberOfShownItems = getNumberOfShownTags(count, data.length);
  const onlyOneShown = numberOfShownItems === 1;

  return (
    <div
      className={clsx(styles.tag_list_container, "flex")}
      ref={containerRef}
      data-testid={dataTestId}
    >
      <RenderWhen condition={Boolean(data.length)} fallback={<NoData />}>
        {data
          .slice(0, numberOfShownItems)
          .map(item =>
            renderTag(item, clsx(onlyOneShown && styles.tag_item_ellipsis))
          )}
      </RenderWhen>
      <RenderWhen condition={Boolean(childrenCount)}>
        <div className="max-content-width ui-inline" ref={childrenRef}>
          {children}
        </div>
      </RenderWhen>
      <OverlayTrigger
        show={isShow}
        delay={200}
        overlay={
          <Popover
            id={styles.tags_popover}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <Popover.Body
              data-testid={dataTestId ? `${dataTestId}_overlay_content` : null}
            >
              {data.slice(count).map(item => (
                <span key={item}>{renderTag(item)}</span>
              ))}
            </Popover.Body>
          </Popover>
        }
      >
        <span
          ref={moreRef}
          onMouseEnter={show}
          onMouseLeave={hide}
          className={clsx(
            styles.more_items,
            "visibility-hidden",
            numberOfShownItems < data.length && "visibility-visible"
          )}
          data-testid={dataTestId ? `${dataTestId}_show_more` : null}
        >
          (+{data.length - count} More)
        </span>
      </OverlayTrigger>
    </div>
  );
};

export default TagList;
